import type { TFunction } from "i18next";
import { chunk } from "lodash";

import {
  ApiSuffixType,
  createErrorResult,
  createSuccessResult,
  CrudApi,
  errors,
  EXCEL_SHEET_NAME_INVALID_CHARS,
  EXCEL_SHEET_NAME_MAX_LENGTH,
  isEntityServerSidePaginated,
  isEntityServerSideSearchable,
  MAX_SEARCH_IDS_BATCH_SIZE,
  rowsFromServerSearchPayload,
  warnAndToastSimpleResult,
  type IExtendableDto,
  type IPaginatedGridSearchPayload,
  type ISearchCriteria,
  type RydeUiUnit,
  type SimpleResult,
} from "@ryde/shared";

export const sanitizeSheetName = (name: string): string =>
  name
    .replace(EXCEL_SHEET_NAME_INVALID_CHARS, " ")
    .trim()
    .slice(0, EXCEL_SHEET_NAME_MAX_LENGTH) || "Sheet1";

export const mergeSelectedAndFetchedRows = ({
  targetIds,
  existingRows,
  fetchedRows,
}: {
  targetIds: readonly number[];
  existingRows: readonly IExtendableDto[];
  fetchedRows: readonly IExtendableDto[];
}): IExtendableDto[] => {
  const rowMap = new Map<number, IExtendableDto>();

  for (const row of existingRows) {
    rowMap.set(row.id, row);
  }

  for (const row of fetchedRows) {
    rowMap.set(row.id, row);
  }

  const result: IExtendableDto[] = [];

  for (const id of targetIds) {
    const row = rowMap.get(id);
    if (row) {
      result.push(row);
    }
  }

  return result;
};

const canFetchMissingEntityDtosByIds = (entity: RydeUiUnit): boolean =>
  isEntityServerSidePaginated(entity) || isEntityServerSideSearchable(entity);

export const fetchMissingEntityDtosByIds = async ({
  entity,
  ids,
  t,
  batchSize = MAX_SEARCH_IDS_BATCH_SIZE,
}: {
  entity: RydeUiUnit;
  ids: number[];
  t: TFunction;
  batchSize?: number;
}): Promise<SimpleResult<IExtendableDto[]>> => {
  if (!ids.length) {
    return createSuccessResult([]);
  }

  if (!canFetchMissingEntityDtosByIds(entity)) {
    return createSuccessResult([]);
  }

  const chunks = chunk(ids, batchSize);
  const results: IExtendableDto[] = [];

  for (const batch of chunks) {
    const body: ISearchCriteria<IExtendableDto> = {
      where: {
        id: { $in: batch },
      },
      take: batch.length,
    };

    const response = await CrudApi.post<
      SimpleResult<IPaginatedGridSearchPayload<IExtendableDto>>
    >({
      entity,
      data: body,
      suffixType: ApiSuffixType.ServerSearch,
    });

    if (!response) {
      const emptyResult = createErrorResult<IExtendableDto[]>(
        errors.common.emptyApiResponse,
      );
      warnAndToastSimpleResult(emptyResult, t);
      return emptyResult;
    }

    if (response.success === false) {
      warnAndToastSimpleResult(response, t);
      return createErrorResult<IExtendableDto[]>(
        errors.common.emptyApiResponse,
        undefined,
        response.error,
      );
    }

    const payload = response.success ? response.data : response;
    const rows = rowsFromServerSearchPayload(payload);

    results.push(...rows);
  }

  return createSuccessResult(results);
};
